import clsx from 'clsx';
import { useEditorRef, useEditorSelector } from 'platejs/react';
import { useDropdown } from '../../hooks/useDropdown';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import styles from './FontFamilyMenu.module.scss';
import { applyMarkFast } from '../applyMarkFast';
import { WORD_FONT_STACKS, fontStackHead } from '../../fontStacks';

// Each option pairs a stable id (used to match the active mark) with a CSS
// font-family stack. The stack is what we write to the Plate mark — passing
// the full stack (not just the display name) means the rendered text gracefully
// falls back when a face isn't installed on the reader's machine.
interface FontOption {
    id: string;
    label: string;
    stack: string;
}

// Gilroy is the brand default — its @font-face is loaded in src/scss/index.scss
// (Gilroy-Regular/Medium/SemiBold/Bold). We pin the dropdown's default to it so
// the editor's base font matches what the user sees in the trigger.
const GILROY_STACK = WORD_FONT_STACKS.gilroy;

// The built-in picker options. Common Microsoft Word faces (Calibri, Cambria,
// Aptos, Segoe UI) are included so imported .docx content — which is overwhelmingly
// authored in these — lights up a real option and renders with the metric-close
// fallbacks defined in fontStacks.ts. Faces a document uses that AREN'T here are
// surfaced dynamically at import time (see documentFontOptions below).
export const FONTS: FontOption[] = [
    { id: 'gilroy', label: 'Gilroy', stack: GILROY_STACK },
    { id: 'inter', label: 'Inter', stack: WORD_FONT_STACKS.inter },
    { id: 'system', label: 'System', stack: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
    { id: 'calibri', label: 'Calibri', stack: WORD_FONT_STACKS.calibri },
    { id: 'cambria', label: 'Cambria', stack: WORD_FONT_STACKS.cambria },
    { id: 'aptos', label: 'Aptos', stack: WORD_FONT_STACKS.aptos },
    { id: 'segoe', label: 'Segoe UI', stack: WORD_FONT_STACKS['segoe ui'] },
    { id: 'arial', label: 'Arial', stack: WORD_FONT_STACKS.arial },
    { id: 'helvetica', label: 'Helvetica', stack: WORD_FONT_STACKS.helvetica },
    { id: 'georgia', label: 'Georgia', stack: WORD_FONT_STACKS.georgia },
    { id: 'times', label: 'Times New Roman', stack: WORD_FONT_STACKS['times new roman'] },
    { id: 'courier', label: 'Courier New', stack: WORD_FONT_STACKS['courier new'] },
    { id: 'mono', label: 'Monospace', stack: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' },
    { id: 'verdana', label: 'Verdana', stack: WORD_FONT_STACKS.verdana },
    { id: 'tahoma', label: 'Tahoma', stack: WORD_FONT_STACKS.tahoma },
    { id: 'pacifico', label: 'Pacifico', stack: "'Pacifico', cursive" },
];

// When no fontFamily mark is on the active leaf, fall back to Gilroy in the
// trigger label — the editor's body CSS uses the same stack so the dropdown's
// label stays truthful for the rendered text.
const DEFAULT_OPTION = FONTS[0];
// Gilroy's own head — lets us tell "no font matched, defaulted to Gilroy"
// apart from "the active font really is Gilroy" (see `current` below).
const DEFAULT_HEAD = fontStackHead(GILROY_STACK);

function titleCase(head: string): string {
    return head.replace(/\b\w/g, (c) => c.toUpperCase());
}

// Walk a fragment collecting the distinct fontFamily stacks present, keyed by
// their first family token (the stable identity matchOption compares on).
function collectDocumentFonts(node: any, into: Map<string, string>): void {
    if (node == null) return;
    if (typeof node.text === 'string') {
        const stack = node.fontFamily;
        if (typeof stack === 'string' && stack.trim()) {
            const head = fontStackHead(stack);
            if (head && !into.has(head)) into.set(head, stack);
        }
        return;
    }
    if (Array.isArray(node.children)) for (const child of node.children) collectDocumentFonts(child, into);
}

// Fonts the current document actually uses that AREN'T one of the built-in
// options — e.g. a Word face we don't ship. Surfaced in the picker so an
// imported font is still selectable and its name shows in the trigger. This is
// the "font families on the go" path: the doc's own fonts populate the menu.
export function documentFontOptions(editor: any): FontOption[] {
    const found = new Map<string, string>();
    const children = editor?.children;
    if (Array.isArray(children)) for (const node of children) collectDocumentFonts(node, found);
    if (found.size === 0) return [];
    const known = new Set(FONTS.map((o) => fontStackHead(o.stack)));
    const out: FontOption[] = [];
    for (const [head, stack] of found) {
        if (!known.has(head)) out.push({ id: `doc:${head}`, label: titleCase(head), stack });
    }
    return out;
}

// Match an active fontFamily mark to one of our options. We compare the
// first family token (case-insensitive) so a saved value like
// `Inter, sans-serif` still matches the `Inter` option even if its stack
// has more fallbacks now than when it was saved. `fonts` defaults to the
// built-in list; callers pass the built-ins + document fonts so an imported
// face resolves to its own dynamic option.
export function matchOption(activeStack: string | undefined, fonts: FontOption[] = FONTS): FontOption {
    if (!activeStack) return DEFAULT_OPTION;
    const head = fontStackHead(activeStack);
    if (!head) return DEFAULT_OPTION;
    const hit = fonts.find((o) => fontStackHead(o.stack) === head);
    return hit ?? DEFAULT_OPTION;
}

export function FontFamilyMenu() {
    const editor = useEditorRef();
    const { open, setOpen, ref: wrapRef } = useDropdown();

    // Leaf mark first, then edge-merged marks — Plate's edge-merged `marks()`
    // drops leaf-only marks when a collapsed caret sits at a leaf boundary
    // (identical bug already fixed for fontSize in FontSizeMenu.tsx). `leaf()`
    // needs the selection passed explicitly — unlike `marks()` it does not
    // default to it.
    const activeStack: string | undefined = useEditorSelector((ed: any) => {
        try {
            const leafEntry = ed?.api?.leaf?.(ed?.selection);
            const leafNode = Array.isArray(leafEntry) ? leafEntry[0] : leafEntry;
            if (leafNode?.fontFamily) return leafNode.fontFamily;
            return ed?.api?.marks?.()?.fontFamily;
        } catch {
            return undefined;
        }
    }, []);
    // Surface the imported document's own fonts alongside the built-ins. Computed
    // as a plain expression (NOT a hook — must not change the render's hook count)
    // and only while the menu is open, so the doc walk happens just during that
    // brief window rather than on every render.
    const dynamicFonts = open ? documentFontOptions(editor) : [];
    const allFonts = dynamicFonts.length ? [...FONTS, ...dynamicFonts] : FONTS;
    const matched = matchOption(activeStack, allFonts);
    // Closed trigger skips the doc walk above, so an active font that isn't a
    // built-in (e.g. an imported doc font) isn't in `allFonts` yet and `matched`
    // falls back to the Gilroy default. Synthesize the same label the open menu
    // would show (documentFontOptions titles a font by its head the same way)
    // instead of running the walk just to render the trigger.
    const activeHead = activeStack ? fontStackHead(activeStack) : undefined;
    const current =
        matched.id === DEFAULT_OPTION.id && activeHead && activeHead !== DEFAULT_HEAD
            ? { ...matched, label: titleCase(activeHead) }
            : matched;

    const apply = (option: FontOption) => (e: React.MouseEvent) => {
        e.preventDefault();
        const ed: any = editor;
        try {
            if (option.id === DEFAULT_OPTION.id) {
                // Picking the default (Gilroy) removes the explicit mark so
                // text inherits the editor's base font — keeps the persisted
                // document clean instead of writing Gilroy on every leaf.
                ed?.tf?.removeMarks?.('fontFamily');
            } else {
                // Single-pass apply — see applyMarkFast for the rationale.
                applyMarkFast(ed, 'fontFamily', option.stack, (v) => ed?.tf?.fontFamily?.addMark?.(v));
            }
        } catch {
            /* defensive */
        }
        setOpen(false);
    };

    return (
        <div className={styles.wrap} ref={wrapRef}>
            <button
                type="button"
                className={clsx(styles.trigger, open && styles.triggerOpen)}
                onMouseDown={(e) => {
                    e.preventDefault();
                    setOpen((o) => !o);
                }}
                aria-haspopup="true"
                aria-expanded={open}
                aria-label="Font family"
                title="Font family"
            >
                <span className={styles.triggerLabel}>{current.label}</span>
                <ArrowDropDownRoundedIcon className={styles.caret} fontSize="small" />
            </button>
            {open && (
                <div className={styles.popover} role="menu" aria-label="Pick font family">
                    {allFonts.map((opt) => (
                        <button
                            key={opt.id}
                            type="button"
                            role="menuitem"
                            className={clsx(styles.item, current.id === opt.id && styles.itemActive)}
                            onMouseDown={apply(opt)}
                            style={opt.stack ? { fontFamily: opt.stack } : undefined}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
